/**
 * Euphoric Bakehouse motion layer.
 *
 * Replaces the reference's Webflow IX2 interactions with GSAP + ScrollTrigger. Two
 * behaviours only, because that is all the reference actually does:
 *   1. fade-and-rise reveal as a section enters the viewport
 *   2. a continuous horizontal marquee inside the tilted band
 *
 * Progressive enhancement is the whole design here. Elements are NOT hidden in CSS by
 * default — `.eb-motion-ready` is added to <html> by this file, and only that class
 * applies `opacity: 0`. If GSAP fails to load, if JS is blocked, or if the user prefers
 * reduced motion, the class is never added and every element renders visible. The
 * reference ships `style="opacity:0"` inline, which leaves content permanently invisible
 * when its JS fails; this avoids that failure mode.
 */

(() => {
  const REDUCED = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  // Captured here, during synchronous parse. By the time start() runs on DOMContentLoaded
  // document.currentScript is null, so reading it later would silently yield no base URL
  // and GSAP would never load.
  const GSAP_BASE = document.currentScript?.dataset.gsapBase || '';

  /** Reveal without animation: just make sure nothing is left hidden. */
  const revealAllNow = () => {
    document.documentElement.classList.remove('eb-motion-ready');
  };

  /**
   * Reveals are triggered by IntersectionObserver and animated by GSAP.
   *
   * Splitting the two matters. IntersectionObserver is the browser's own, off-main-thread
   * visibility primitive — far cheaper than a scroll handler for a one-shot reveal, and it
   * keeps working if GSAP never arrives (the fallback below just clears the hidden state).
   * GSAP is then only responsible for the easing and stagger, which is what it is good at.
   */
  const initReveals = (gsap) => {
    const groups = document.querySelectorAll('[data-eb-reveal-group]');
    if (!groups.length) return;

    if (!('IntersectionObserver' in window)) {
      revealAllNow();
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (!entry.isIntersecting) continue;

          const group = entry.target;
          observer.unobserve(group);

          const items = group.querySelectorAll('.eb-reveal');
          if (!items.length) continue;

          if (gsap) {
            gsap.to(items, {
              opacity: 1,
              y: 0,
              duration: Number(group.dataset.ebRevealDuration) || 0.7,
              stagger: Number(group.dataset.ebRevealStagger) || 0.08,
              ease: 'power2.out',
            });
          } else {
            for (const item of items) {
              item.style.opacity = '1';
              item.style.transform = 'none';
            }
          }
        }
      },
      // Fires a little before the group's top edge reaches the fold, so the animation is
      // already underway by the time it is properly in view.
      { rootMargin: '0px 0px -15% 0px', threshold: 0.01 }
    );

    // Read a layout property to flush the opacity:0 state to the compositor first.
    void document.documentElement.offsetHeight;

    for (const group of groups) observer.observe(group);
  };

  /**
   * Marquee scroll-reactivity.
   *
   * The scroll itself is a CSS animation on the track, so it runs with no JS. This only
   * adds the reference's behaviour of speeding up while the page is being scrolled, by
   * varying `animation-duration` — nothing here is required for the band to move.
   */
  const initMarquees = (gsap) => {
    if (!window.ScrollTrigger) return;

    for (const marquee of document.querySelectorAll('[data-eb-marquee]')) {
      if (marquee.dataset.ebMarqueeScrub !== 'true') continue;
      if (marquee.dataset.ebScrubBound === 'true') continue;

      const track = marquee.querySelector('[data-eb-marquee-track]');
      if (!track) continue;

      marquee.dataset.ebScrubBound = 'true';

      const base = parseFloat(getComputedStyle(track).animationDuration) || 28;
      let settle;

      window.ScrollTrigger.create({
        trigger: marquee,
        start: 'top bottom',
        end: 'bottom top',
        onUpdate: (self) => {
          // Shorter duration = faster. Cap the boost so fast flicks stay legible.
          const boost = 1 + Math.min(Math.abs(self.getVelocity()) / 2200, 2.2);
          track.style.animationDuration = `${base / boost}s`;

          clearTimeout(settle);
          settle = setTimeout(() => {
            track.style.animationDuration = `${base}s`;
          }, 180);
        },
      });
    }
  };

  /**
   * Load GSAP only on pages that actually animate.
   *
   * GSAP + ScrollTrigger is ~115KB. Emitting it on every page would tax templates that
   * have no motion at all. Instead this module ships everywhere (it is tiny), checks
   * whether the page contains anything to animate, and pulls GSAP in only if so.
   * Classic scripts execute once per unique src, so the guard also prevents double-loads.
   */
  const loadScript = (src) =>
    new Promise((resolve, reject) => {
      const existing = document.querySelector(`script[src="${src}"]`);
      if (existing) {
        if (existing.dataset.loaded === 'true') resolve();
        else existing.addEventListener('load', () => resolve(), { once: true });
        existing.addEventListener('error', reject, { once: true });
        return;
      }
      const el = document.createElement('script');
      el.src = src;
      el.defer = true;
      el.addEventListener(
        'load',
        () => {
          el.dataset.loaded = 'true';
          resolve();
        },
        { once: true }
      );
      el.addEventListener('error', reject, { once: true });
      document.head.appendChild(el);
    });

  const needsMotion = () =>
    document.querySelector('[data-eb-marquee], [data-eb-reveal-group]') !== null;

  const initParallax = (gsap) => {
    if (!window.ScrollTrigger) return;

    for (const media of document.querySelectorAll('[data-eb-parallax]')) {
      if (media.dataset.ebParallaxBound === 'true') continue;
      media.dataset.ebParallaxBound = 'true';

      const depth = Number(media.dataset.ebParallax) || 12;

      gsap.fromTo(
        media.querySelector('img') || media,
        { yPercent: -depth / 2 },
        {
          yPercent: depth / 2,
          ease: 'none',
          scrollTrigger: { trigger: media, start: 'top bottom', end: 'bottom top', scrub: true },
        }
      );
    }
  };

  const start = async () => {
    if (REDUCED || !needsMotion()) {
      revealAllNow();
      return;
    }

    document.documentElement.classList.add('eb-motion-ready');

    // Reveals do not depend on GSAP — IntersectionObserver drives them, and the CSS
    // transition on .eb-reveal covers the case where GSAP never arrives. Start them
    // immediately rather than behind the network, so a slow CDN cannot leave content
    // sitting at opacity 0.
    requestAnimationFrame(() => initReveals(window.gsap || null));

    if (!window.gsap && GSAP_BASE) {
      try {
        await loadScript(`${GSAP_BASE}gsap.min.js`);
        await loadScript(`${GSAP_BASE}gsap-scrolltrigger.min.js`);
      } catch {
        // Marquees stay static. Everything else already works.
        return;
      }
    }

    const gsap = window.gsap;
    if (!gsap) return;

    if (gsap.registerPlugin && window.ScrollTrigger) {
      gsap.registerPlugin(window.ScrollTrigger);
    }

    initMarquees(gsap);
    initParallax(gsap);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start, { once: true });
  } else {
    start();
  }

  // The theme editor re-renders sections without a page load; re-init so new blocks move.
  document.addEventListener('shopify:section:load', () => {
    if (REDUCED) return;
    requestAnimationFrame(() => {
      initReveals(window.gsap || null);
      if (window.gsap) {
        initMarquees(window.gsap);
        initParallax(window.gsap);
      }
    });
  });
})();
